import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import B2bPendingPaymentsApi from 'api/b2b/PendingPayments'
import B2bTDPAPI from 'api/b2b/TdpApi'
import OrbitSession from 'hooks/useCookies'
import useDebounce from 'hooks/useDebounce'

import InvoiceTable from './InvoiceList'
import PendingPaymentTable from './PendingPaymentList'

interface Company {
  tenant_id: string
  company_name: string
  id: string
}

interface TenantData {
  data: Company[]
}

interface CompanyOption {
  label: string
  value: string
  companyId: string
}

interface FormData {
  companyName: CompanyOption | null
  destination: string
  orderStartDate: string
  orderEndDate: string
  dueDate: string
}

export type PendingPaymentTableProps = {
  company_name: string
  company_id: string
  currency: string
  wallet_balance: number
  outstanding_dues: number
}

export type InvoiceDetailsTableProps = {
  company_name: string
  order_id: string
  product: string
  order_date: string
  package_name: number
  due_date: string
  currency: string
  order_amount: number
  paid_amount: number
  pending_amount: number
  item_count: number
}

// Custom hook for debounced value

export default function PendingPaymentSearchForm() {
  const [companySearchTerm, setCompanySearchTerm] = useState('')
  const [tableData, setTableData] = useState<PendingPaymentTableProps[]>([])
  const [invoiceTableData, setInvoiceTableData] = useState<
    InvoiceDetailsTableProps[]
  >([])
  const debouncedSearchTerm = useDebounce(companySearchTerm, 500) // 500ms debounce
  const { control, handleSubmit, reset, watch } = useForm<FormData>({
    defaultValues: {
      companyName: null,
      destination: '',
      orderStartDate: '',
      orderEndDate: '',
      dueDate: ''
    }
  })

  // Watch company name for search term updates
  const companyNameInput = watch('companyName')

  // Query for fetching companies based on search term
  const {
    data: tenantData,
    isLoading: isTenantLoading,
    isError: tenantFetchError
    // error: tenantError
  } = useQuery<TenantData>(
    ['tenant-search', debouncedSearchTerm],
    async () => {
      const token = OrbitSession.get('faberToken')
      const { data } = await B2bTDPAPI.getTDPDetails(token, {
        company_name: debouncedSearchTerm
      })
      return data
    },
    {
      enabled: debouncedSearchTerm.length >= 2, // Only search when user types at least 2 characters
      staleTime: 2 * 60 * 1000, // Cache for 2 minutes
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('Failed to fetch companies:', error)
      }
    }
  )

  // Mutation for pending payment search
  const { mutate: searchPendingPayments } = useMutation({
    mutationFn: async (params: Record<string, string>) => {
      const token = OrbitSession.get('faberToken')
      return await B2bPendingPaymentsApi.getPendingPayments(token, params)
    },
    onSuccess: (data) => {
      if (
        data?.data?.wallet_summary?.length === 0 ||
        !data?.data.invoice_details
      ) {
        toast.info('No data found', { autoClose: 3000, hideProgressBar: false })
        return
      }
      setTableData([data.data.wallet_summary])
      setInvoiceTableData(data.data.invoice_details)
    },
    onError: () => {
      toast.error('Failed to fetch pending payments')
    }
  })

  // Memoized company options
  const companyOptions = useMemo((): CompanyOption[] => {
    if (!tenantData || !Array.isArray(tenantData)) return []

    return tenantData.map((company: Company) => ({
      label: company.company_name || '',
      value: company.company_name || '',
      companyId: company.id || ''
    }))
  }, [tenantData])

  // Handle input change for company search
  const handleCompanyInputChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setCompanySearchTerm(value)
    },
    []
  )

  const onSubmit = (data: FormData) => {
    // Create cleaned data object - only include dates if they are selected
    const cleanedData: Record<string, string> = {
      company_id: data.companyName?.companyId || '',
      destination: data.destination
    }
    // TODO:Can we think of something better for this if block
    // Only include dates if they have values
    if (data.orderStartDate) {
      cleanedData.order_start_date = data.orderStartDate
    }
    if (data.orderEndDate) {
      cleanedData.order_end_date = data.orderEndDate
    }
    if (data.dueDate) {
      cleanedData.due_date = data.dueDate
    }
    searchPendingPayments(cleanedData)
  }

  const handleReset = () => {
    reset({
      companyName: null,
      destination: '',
      orderStartDate: '',
      orderEndDate: '',
      dueDate: ''
    })
    setCompanySearchTerm('')
    setTableData([])
    setInvoiceTableData([])
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Search Form */}
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 2,
          mb: 3,
          p: 3,
          bgcolor: '#f7faff',
          borderRadius: 2
        }}
      >
        {/* Company Name with Debounced Search */}
        <Controller
          name='companyName'
          control={control}
          rules={{
            required: { value: true, message: 'Company name is required' }
          }}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              options={companyOptions}
              loading={isTenantLoading}
              getOptionLabel={(option: CompanyOption | null) =>
                option?.label || ''
              }
              isOptionEqualToValue={(
                option: CompanyOption,
                value: CompanyOption | null
              ) => option?.value === value?.value}
              onChange={(_, data: CompanyOption | null) => field.onChange(data)}
              onInputChange={handleCompanyInputChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Company Name *'
                  variant='outlined'
                  fullWidth
                  error={!!error}
                  helperText={
                    error?.message ||
                    (debouncedSearchTerm.length > 0 &&
                    debouncedSearchTerm.length < 2
                      ? 'Type at least 2 characters to search'
                      : '')
                  }
                  placeholder='Type company name to search...'
                  InputProps={{
                    ...params.InputProps
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component='li' {...props}>
                  <Box>
                    <Typography variant='body1'>{option.label}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      ID: {option.companyId}
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText={
                debouncedSearchTerm.length < 2
                  ? 'Type at least 2 characters to search'
                  : isTenantLoading
                  ? 'Searching...'
                  : tenantFetchError
                  ? 'Error loading companies'
                  : 'No companies found'
              }
            />
          )}
        />

        {/* Destination */}
        <Controller
          name='destination'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label='Destination'
              variant='outlined'
              fullWidth
              placeholder='Enter destination'
            />
          )}
        />

        {/* Order Start Date */}
        <Controller
          name='orderStartDate'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type='date'
              label='Order Start Date'
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              fullWidth
            />
          )}
        />

        {/* Order End Date */}
        <Controller
          name='orderEndDate'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type='date'
              label='Order End Date'
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              fullWidth
            />
          )}
        />

        {/* Due Date */}
        <Controller
          name='dueDate'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type='date'
              label='Due Date'
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              fullWidth
            />
          )}
        />

        {/* Buttons */}
        <Box
          sx={{
            gridColumn: '1 / -1',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end'
          }}
        >
          <Button
            variant='outlined'
            color='secondary'
            onClick={handleReset}
            type='button'
          >
            Reset
          </Button>
          <Button
            variant='contained'
            color='primary'
            type='submit'
            disabled={!companyNameInput?.value}
          >
            Search
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />
      <Box>
        <PendingPaymentTable data={tableData} />
      </Box>
      <Box>
        <InvoiceTable data={invoiceTableData} />
      </Box>


      {/* Error Display */}
      {tenantFetchError && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
          <Typography color='error' variant='body2'>
            Error loading data: {'Unknown error'}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
